import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Assessment,
  Delete,
  Add,
  Save,
  Description,
  Map,
  TrendingUp,
  BarChart,
} from '@mui/icons-material';
import { Header } from '../components/layout/Header';
import api from '../services/api';
import { uploadService } from '../services/uploadService';
import { predictionService } from '../services/predictionService';
import { format } from 'date-fns';

interface SheetPreview {
  sheetName: string;
  sheetIndex: number;
  totalRows: number;
  columnHeaders: string[];
  sampleData: string[][];
}

interface MappingTemplate {
  id: number;
  name: string;
  description?: string;
  createdAt: string;
  isDefault?: boolean;
}

interface Prediction {
  id: number;
  modelName: string;
  status: string;
  createdAt: string;
  completedAt?: string;
  errorMessage?: string;
}

interface FileDetail {
  id: number;
  fileName: string;
  fileSize: number;
  status: string;
  uploadedAt: string;
  errorMessage?: string;
  mappingTemplate?: MappingTemplate;
}

export const FileDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [fileDetail, setFileDetail] = useState<FileDetail | null>(null);
  const [sheets, setSheets] = useState<SheetPreview[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [mappingTemplates, setMappingTemplates] = useState<MappingTemplate[]>([]);
  const [selectedMappingId, setSelectedMappingId] = useState<number | ''>('');
  const [predictions, setPredictions] = useState<Prediction[]>([]);

  useEffect(() => {
    if (id) {
      loadFileDetail();
    }
  }, [id]);

  const loadFileDetail = async () => {
    try {
      setLoading(true);
      setError('');

      // Load file info, preview, templates, and predictions in parallel
      const [fileResponse, previewResponse, templatesResponse, predictionsResponse] = await Promise.all([
        uploadService.getUpload(parseInt(id!)),
        api.post('/mappings/preview', {
          uploadId: parseInt(id!),
          maxRows: 10,
        }),
        api.get('/mappings/templates'),
        predictionService.getPredictions(),
      ]);

      setFileDetail({
        ...fileResponse,
        mappingTemplate: fileResponse.mappingTemplate ? {
          id: fileResponse.mappingTemplate.id,
          name: fileResponse.mappingTemplate.name,
          createdAt: new Date().toISOString(),
          isDefault: false,
        } : undefined
      });

      if (fileResponse.mappingTemplate) {
        setSelectedMappingId(fileResponse.mappingTemplate.id);
      }

      setSheets(previewResponse.data.sheets || []);
      setMappingTemplates(templatesResponse.data || []);

      // Filter predictions for this file
      const filePredictions = predictionsResponse.filter(
        (p: any) => p.uploadedFile?.id === parseInt(id!)
      );
      setPredictions(filePredictions);

      setLoading(false);
    } catch (err: any) {
      console.error('Failed to load file detail:', err);
      setError(err.response?.data?.error || 'Nem sikerült betölteni a fájl részleteit');
      setLoading(false);
    }
  };

  const handleAssignMapping = async () => {
    if (!selectedMappingId || !id) return;

    try {
      setSaving(true);
      await api.put(`/mappings/uploads/${id}/template/${selectedMappingId}`);

      // Reload file detail to get updated mapping
      await loadFileDetail();

      alert('Mapping sikeresen hozzárendelve!');
    } catch (err: any) {
      console.error('Failed to assign mapping:', err);
      alert(err.response?.data?.error || 'Nem sikerült hozzárendelni a mapping-et');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFile = async () => {
    if (!fileDetail) return;

    if (!window.confirm(`Biztosan törölni szeretnéd a "${fileDetail.fileName}" fájlt?`)) {
      return;
    }

    try {
      await uploadService.deleteUpload(fileDetail.id);
      alert('Fájl sikeresen törölve!');
      navigate('/auction-files');
    } catch (err: any) {
      console.error('Failed to delete file:', err);
      alert(err.response?.data?.error || 'Nem sikerült törölni a fájlt');
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      </>
    );
  }

  if (error || !fileDetail) {
    return (
      <>
        <Header />
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Fájl nem található'}
          </Alert>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/auction-files')}>
            Vissza a fájlokhoz
          </Button>
        </Container>
      </>
    );
  }

  const currentSheet = sheets[activeSheet];

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/auction-files')}
              sx={{ mb: 2 }}
            >
              Vissza
            </Button>
            <Typography variant="h4" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description /> {fileDetail.fileName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip
                label={fileDetail.status}
                color={fileDetail.status === 'UPLOADED' ? 'success' : 'default'}
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                Feltöltve: {format(new Date(fileDetail.uploadedAt), 'yyyy-MM-dd HH:mm')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Méret: {(fileDetail.fileSize / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleDeleteFile}
            >
              Törlés
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Mapping Selection */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Map /> Mapping Hozzárendelés
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {fileDetail.mappingTemplate && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Jelenleg hozzárendelt mapping:</strong> {fileDetail.mappingTemplate.name}
                  </Typography>
                  {fileDetail.mappingTemplate.description && (
                    <Typography variant="caption" color="text.secondary">
                      {fileDetail.mappingTemplate.description}
                    </Typography>
                  )}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 300 }}>
                  <InputLabel>Mapping Sablon</InputLabel>
                  <Select
                    value={selectedMappingId}
                    onChange={(e) => setSelectedMappingId(e.target.value as number)}
                    label="Mapping Sablon"
                  >
                    <MenuItem value="">
                      <em>Nincs kiválasztva</em>
                    </MenuItem>
                    {mappingTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                        {template.description && ` - ${template.description}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                  onClick={handleAssignMapping}
                  disabled={!selectedMappingId || saving}
                >
                  Hozzárendelés
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => navigate(`/mapping-editor?uploadId=${fileDetail.id}`)}
                >
                  Új Mapping Létrehozása
                </Button>

                {fileDetail.mappingTemplate && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/mapping-editor?templateId=${fileDetail.mappingTemplate!.id}&uploadId=${fileDetail.id}`)}
                  >
                    Mapping Szerkesztése
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Predictions Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp /> Előrejelzések
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ehhez a fájlhoz készített cash-flow előrejelzések
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Assessment />}
                  onClick={() => navigate('/pricing', { state: { uploadId: fileDetail.id } })}
                >
                  Új Előrejelzés
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {predictions.length === 0 ? (
                <Alert severity="info" sx={{ textAlign: 'center' }}>
                  Még nincs előrejelzés ehhez a fájlhoz. Készíts egyet az "Új Előrejelzés" gombbal!
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Modell</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Létrehozva</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Befejezve</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Műveletek</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {predictions.map((prediction) => (
                        <TableRow
                          key={prediction.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/predictions/${prediction.id}`)}
                        >
                          <TableCell>#{prediction.id}</TableCell>
                          <TableCell>
                            <Chip
                              label={prediction.status}
                              size="small"
                              color={
                                prediction.status === 'COMPLETED' ? 'success' :
                                prediction.status === 'RUNNING' || prediction.status === 'PENDING' ? 'warning' :
                                prediction.status === 'FAILED' ? 'error' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>{prediction.modelName}</TableCell>
                          <TableCell>{format(new Date(prediction.createdAt), 'yyyy-MM-dd HH:mm')}</TableCell>
                          <TableCell>
                            {prediction.completedAt ? format(new Date(prediction.completedAt), 'yyyy-MM-dd HH:mm') : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/predictions/${prediction.id}`);
                              }}
                            >
                              <Assessment />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Excel Preview */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChart /> Fájl Tartalma
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {sheets.length === 0 ? (
                <Alert severity="warning">
                  Nem sikerült betölteni a fájl tartalmát
                </Alert>
              ) : (
                <>
                  {/* Sheet Tabs */}
                  <Tabs
                    value={activeSheet}
                    onChange={(_, newValue) => setActiveSheet(newValue)}
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                  >
                    {sheets.map((sheet, index) => (
                      <Tab
                        key={index}
                        label={
                          <Box>
                            {sheet.sheetName}
                            <Chip
                              label={`${sheet.totalRows} sor`}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Box>
                        }
                      />
                    ))}
                  </Tabs>

                  {/* Current Sheet Preview */}
                  {currentSheet && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {currentSheet.columnHeaders.length} oszlop • Első 10 sor előnézete
                        </Typography>
                      </Box>

                      <TableContainer sx={{ maxHeight: 600, overflow: 'auto' }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                                  borderBottom: 2,
                                  borderColor: 'divider',
                                  zIndex: 1,
                                }}
                              >
                                #
                              </TableCell>
                              {currentSheet.columnHeaders.map((header, index) => (
                                <TableCell
                                  key={index}
                                  sx={{
                                    fontWeight: 600,
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                                    borderBottom: 2,
                                    borderColor: 'divider',
                                    zIndex: 1,
                                  }}
                                >
                                  {header || `Column ${index + 1}`}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentSheet.sampleData.map((row, rowIndex) => (
                              <TableRow key={rowIndex} hover>
                                <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                  {rowIndex + 1}
                                </TableCell>
                                {row.map((cell, cellIndex) => (
                                  <TableCell key={cellIndex}>
                                    {cell || <em style={{ color: '#999' }}>null</em>}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
